/**
 * PublicRoute.js - Route protection component for public/unauthenticated routes
 *
 * Provides reverse authentication protection for public routes:
 * - Allows access to public routes (login, reset password) for unauthenticated users
 * - Redirects authenticated users away from public routes to their intended destination
 * - Preserves intended destination from previous navigation attempts
 * - Avoids full-screen loading UI during authentication state checks
 * - Prevents authenticated users from accessing login/signup flows
 * - Handles post-authentication redirects smoothly
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';

/**
 * PublicRoute - Route wrapper for public/unauthenticated routes
 *
 * @param {{ children: React.ReactNode }} props - Component props
 * @returns {React.ReactNode}
 *
 * Authentication flow:
 * 1. Shows loading spinner while checking auth state
 * 2. Redirects authenticated users to their intended destination
 * 3. Allows unauthenticated users to access public routes
 * 4. Handles post-login redirects from saved navigation state
 *
 * This component prevents authenticated users from accessing public routes
 * like login and signup pages, providing a better user experience.
 */
export const PublicRoute = ({ children }) => {
  // Get auth state from context with local typing for TS-in-JS consumers
  /** @type {{ user: any, loading: boolean }} */
  const auth = /** @type {any} */ (useAuth());
  const { user, loading } = auth;
  const location = useLocation(); // Current route location

  // While authentication state is being determined, render nothing.
  // SplashScreen is shown at App.js level to ensure minimum display time.
  if (loading) {
    return null;
  }

  // Redirect authenticated users away from public routes
  if (user) {
    // Use the saved destination from ProtectedRoute if it exists, otherwise default to /today
    const from = location.state?.from?.pathname || '/today';
    return <Navigate to={from} replace />;
  }

  // Allow unauthenticated users to access public routes (login, reset password, etc.)
  return children;
};
